import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { auth, db } from "../firebase";
import { AlertCircle, Check, Download, Mail, RefreshCw, Search } from "lucide-react";

const API_BASE = "https://jabuzwane.vercel.app/api";
const OUTSTANDING_BOOKING_STATUSES = new Set(["unpaid", "failed", "processing"]);
const OUTSTANDING_ORDER_STATUSES = new Set(["pending", "failed", "processing"]);
const DATE_FILTERS = [
	{ id: "all", label: "All time" },
	{ id: "thisWeek", label: "This week" },
	{ id: "thisMonth", label: "This month" },
	{ id: "previousMonth", label: "Previous month" },
];

function timestampDate(value) {
	if (!value) return null;
	const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
}

function getDateRange(filter, now = new Date()) {
	if (filter === "all") return null;

	const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	if (filter === "thisWeek") {
		const daysSinceMonday = (startOfToday.getDay() + 6) % 7;
		const start = new Date(startOfToday);
		start.setDate(start.getDate() - daysSinceMonday);
		const end = new Date(start);
		end.setDate(end.getDate() + 7);
		return { start, end };
	}

	const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
	if (filter === "thisMonth") {
		return {
			start: startOfThisMonth,
			end: new Date(now.getFullYear(), now.getMonth() + 1, 1),
		};
	}

	return {
		start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
		end: startOfThisMonth,
	};
}

function isWithinDateRange(row, range) {
	if (!range) return true;
	const date = timestampDate(row.createdAt);
	return date !== null && date >= range.start && date < range.end;
}

function formatDate(value) {
	const date = timestampDate(value);
	return date ? date.toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }) : "—";
}

function getCustomerName(record) {
	return [record.name || record.buyerName, record.surname || record.buyerSurname]
		.filter(Boolean)
		.join(" ")
		.trim() || "Unnamed customer";
}

function makeOutstandingRows(snapshots) {
	const [bookings, books, tickets] = snapshots;
	const rows = [];

	bookings.forEach((docSnap) => {
		const booking = docSnap.data();
		const paymentStatus = booking.paymentStatus || "unpaid";
		if (booking.status === "cancelled" || booking.quoteOnly === true
			|| !OUTSTANDING_BOOKING_STATUSES.has(paymentStatus)) return;
		const payAtAppointment = booking.paymentChoice === "later"
			|| booking.onlinePaymentEnabled === false;
		const legacyManualPaymentCandidate = !booking.paymentChoice && !booking.checkoutId;
		rows.push({
			id: docSnap.id,
			type: "Appointment",
			customer: getCustomerName(booking),
			email: booking.email || booking.buyerEmail || "",
			phone: booking.cellphone || booking.buyerPhone || "",
			item: booking.serviceTitle || "Appointment",
			status: paymentStatus,
			canMarkOnPremPaid: paymentStatus === "unpaid"
				&& booking.status === "confirmed"
				&& (payAtAppointment || legacyManualPaymentCandidate)
				&& !booking.checkoutId,
			paymentMethod: payAtAppointment
				? "Pay at appointment"
				: legacyManualPaymentCandidate
					? "No online checkout · confirm payment method"
				: booking.checkoutId
					? "Online checkout started"
					: "Online payment not started",
			createdAt: booking.bookedAt || booking.createdAt,
			appointment: [booking.date, booking.time].filter(Boolean).join(" "),
		});
	});

	books.forEach((docSnap) => {
		const order = docSnap.data();
		const status = order.status || "pending";
		if (!OUTSTANDING_ORDER_STATUSES.has(status)) return;
		rows.push({
			id: docSnap.id,
			type: "Book",
			customer: getCustomerName(order),
			email: order.buyerEmail || "",
			phone: order.buyerPhone || "",
			item: order.bookTitle || "Book order",
			status,
			paymentMethod: order.checkoutId ? "Online checkout started" : "Payment not completed",
			createdAt: order.createdAt,
			appointment: order.format === "hardCopy" ? "Hard copy" : "Online PDF",
		});
	});

	tickets.forEach((docSnap) => {
		const order = docSnap.data();
		const status = order.status || "pending";
		if (!OUTSTANDING_ORDER_STATUSES.has(status)) return;
		rows.push({
			id: docSnap.id,
			type: "Event ticket",
			customer: getCustomerName(order),
			email: order.buyerEmail || "",
			phone: order.buyerPhone || "",
			item: [order.eventTitle, order.tierName].filter(Boolean).join(" — ") || "Event ticket",
			status,
			paymentMethod: order.checkoutId ? "Online checkout started" : "Payment not completed",
			createdAt: order.createdAt,
			appointment: `${Number(order.quantity) || 1} ticket${Number(order.quantity) === 1 ? "" : "s"}`,
		});
	});

	return rows.sort((a, b) => {
		const dateA = timestampDate(a.createdAt)?.getTime() || 0;
		const dateB = timestampDate(b.createdAt)?.getTime() || 0;
		return dateA - dateB;
	});
}

function csvValue(value) {
	return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function downloadCsv(rows) {
	const headers = [
		"Type", "Customer", "Email", "Phone", "Item", "Payment status",
		"Payment method", "Created", "Details", "Reference",
	];
	const lines = rows.map((row) => [
		row.type,
		row.customer,
		row.email,
		row.phone,
		row.item,
		row.status,
		row.paymentMethod,
		formatDate(row.createdAt),
		row.appointment,
		row.id,
	].map(csvValue).join(","));
	const csv = [headers.map(csvValue).join(","), ...lines].join("\r\n");
	const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = `unpaid-customers-${new Date().toISOString().slice(0, 10)}.csv`;
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	URL.revokeObjectURL(url);
}

const UnpaidCustomersTab = ({ adminEmail }) => {
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [search, setSearch] = useState("");
	const [dateFilter, setDateFilter] = useState("all");
	const [typeFilter, setTypeFilter] = useState("all");
	const [statusFilter, setStatusFilter] = useState("all");
	const [recipient, setRecipient] = useState("");
	const [reportEnabled, setReportEnabled] = useState(false);
	const [savingReport, setSavingReport] = useState(false);
	const [reportNotice, setReportNotice] = useState("");
	const [markingPaidId, setMarkingPaidId] = useState("");
	const [paymentNotice, setPaymentNotice] = useState("");

	const loadRows = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const snapshots = await Promise.all([
				getDocs(collection(db, "bookings")),
				getDocs(query(
					collection(db, "bookOrders"),
					where("status", "in", [...OUTSTANDING_ORDER_STATUSES])
				)),
				getDocs(query(
					collection(db, "ticketOrders"),
					where("status", "in", [...OUTSTANDING_ORDER_STATUSES])
				)),
			]);
			setRows(makeOutstandingRows(snapshots));
		} catch (err) {
			console.error("Could not load unpaid customer records:", err);
			setError(err.message || "Could not load unpaid customer records.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadRows();
	}, [loadRows]);

	useEffect(() => {
		let cancelled = false;
		const loadReportSettings = async () => {
			try {
				const token = await auth.currentUser?.getIdToken();
				if (!token) return;
				const response = await fetch(`${API_BASE}/notify-booking?reportSettings=1`, {
					headers: { Authorization: `Bearer ${token}` },
				});
				const result = await response.json();
				if (!response.ok) throw new Error(result.message || "Could not load daily report settings.");
				if (!cancelled) {
					setRecipient(result.recipientEmail || adminEmail || "");
					setReportEnabled(Boolean(result.recipientEmail));
				}
			} catch (err) {
				if (!cancelled) setReportNotice(err.message || "Could not load daily report settings.");
			}
		};
		loadReportSettings();
		return () => { cancelled = true; };
	}, [adminEmail]);

	const dateFilteredRows = useMemo(() => {
		const range = getDateRange(dateFilter);
		return rows.filter((row) => isWithinDateRange(row, range));
	}, [rows, dateFilter]);

	const filteredRows = useMemo(() => {
		const needle = search.trim().toLowerCase();
		return dateFilteredRows.filter((row) => {
			if (typeFilter !== "all" && row.type !== typeFilter) return false;
			if (statusFilter !== "all" && row.status !== statusFilter) return false;
			if (!needle) return true;
			return [row.customer, row.email, row.phone, row.item, row.id]
				.some((value) => String(value).toLowerCase().includes(needle));
		});
	}, [dateFilteredRows, search, statusFilter, typeFilter]);

	const totals = useMemo(() => ({
		count: dateFilteredRows.length,
		awaiting: dateFilteredRows.filter((row) => row.status === "unpaid" || row.status === "pending").length,
		processing: dateFilteredRows.filter((row) => row.status === "processing").length,
		failed: dateFilteredRows.filter((row) => row.status === "failed").length,
	}), [dateFilteredRows]);

	const saveReportSettings = async () => {
		setSavingReport(true);
		setReportNotice("");
		try {
			const token = await auth.currentUser?.getIdToken();
			if (!token) throw new Error("Your admin session has expired. Please sign in again.");
			const response = await fetch(`${API_BASE}/notify-booking?reportSettings=1`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ reportSettingsAction: "save" }),
			});
			const result = await response.json();
			if (!response.ok) throw new Error(result.message || "Could not enable the daily report.");
			setReportEnabled(true);
			setRecipient(result.recipientEmail || recipient);
			setReportNotice("Recipient saved successfully. Automatic delivery also requires the API and daily Cron job to be deployed in Vercel with CRON_SECRET configured.");
		} catch (err) {
			setReportNotice(err.message || "Could not enable the daily report.");
		} finally {
			setSavingReport(false);
		}
	};

	const markAppointmentPaid = async (row) => {
		if (!row.canMarkOnPremPaid || markingPaidId) return;
		setMarkingPaidId(row.id);
		setPaymentNotice("");
		try {
			const token = await auth.currentUser?.getIdToken();
			if (!token) throw new Error("Your admin session has expired. Please sign in again.");
			const response = await fetch(`${API_BASE}/notify-booking`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ paymentAction: "markOnPremPaid", bookingId: row.id }),
			});
			const result = await response.json();
			if (!response.ok) throw new Error(result.message || "Could not update this appointment.");
			await loadRows();
			setPaymentNotice(`${row.customer}'s appointment is now marked as paid.`);
		} catch (err) {
			console.error("Could not mark appointment as paid:", err);
			setPaymentNotice(err.message || "Could not update this appointment.");
		} finally {
			setMarkingPaidId("");
		}
	};

	return (
		<section className="adm-unpaid">
			<div className="adm-commercial__header">
				<div>
					<div className="adm-panel__eyebrow">Outstanding payments</div>
					<h1>Unpaid customers</h1>
					<p>Review appointments, book orders, and event tickets that still need payment.</p>
				</div>
				<div className="adm-unpaid__actions">
					<button type="button" className="adm-unpaid__button adm-unpaid__button--secondary" onClick={loadRows} disabled={loading}>
						<RefreshCw size={16} className={loading ? "is-spinning" : ""} /> Refresh
					</button>
					<button type="button" className="adm-unpaid__button" onClick={() => downloadCsv(filteredRows)} disabled={!filteredRows.length}>
						<Download size={16} /> Download CSV
					</button>
				</div>
			</div>

			<div className="adm-commercial__stats">
				<div><span>Outstanding records</span><strong>{totals.count}</strong></div>
				<div><span>Awaiting payment</span><strong>{totals.awaiting}</strong></div>
				<div><span>Processing payments</span><strong>{totals.processing}</strong></div>
				<div><span>Failed payments</span><strong>{totals.failed}</strong></div>
			</div>

			<div className="adm-unpaid__report">
				<div className="adm-unpaid__report-icon"><Mail size={19} /></div>
				<div className="adm-unpaid__report-copy">
					<strong>{reportEnabled ? "Daily follow-up recipient is saved" : "Get payment and delivery follow-ups by email each evening"}</strong>
					<p>Includes unpaid purchases and paid hard-copy book orders with no delivery update for 2+ days. The Vercel Cron job sends it daily at 18:00 South African time once configured.</p>
					{reportNotice && <span className="adm-unpaid__notice" role="status">{reportNotice}</span>}
				</div>
				<div className="adm-unpaid__report-controls">
					<input
						type="email"
						value={recipient}
						placeholder={adminEmail || "Admin email"}
						aria-label="Daily report recipient email"
						readOnly
					/>
					<button type="button" className="adm-unpaid__button" onClick={saveReportSettings} disabled={savingReport || !recipient.trim()}>
						{savingReport ? "Saving…" : reportEnabled ? "Update email" : "Enable report"}
					</button>
				</div>
			</div>

			{paymentNotice && <div className="adm-unpaid__notice" role="status">{paymentNotice}</div>}
			<div className="adm-unpaid__date-filter">
				<div className="adm-unpaid__date-options" role="group" aria-label="Filter unpaid records by date">
					{DATE_FILTERS.map((filter) => (
						<button
							type="button"
							key={filter.id}
							className={dateFilter === filter.id ? "is-active" : ""}
							aria-pressed={dateFilter === filter.id}
							onClick={() => setDateFilter(filter.id)}
						>
							{filter.label}
						</button>
					))}
				</div>
				<span>Changing this filter only searches the records already loaded; it does not make additional Firestore reads.</span>
			</div>
			<div className="adm-unpaid__filters">
				<label className="adm-unpaid__search"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search customer, email, item, or reference" /></label>
				<select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} aria-label="Filter by purchase type">
					<option value="all">All types</option>
					<option value="Appointment">Appointments</option>
					<option value="Book">Books</option>
					<option value="Event ticket">Event tickets</option>
				</select>
				<select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Filter by payment status">
					<option value="all">All payment statuses</option>
					<option value="unpaid">Unpaid</option>
					<option value="pending">Pending</option>
					<option value="processing">Processing</option>
					<option value="failed">Failed</option>
				</select>
			</div>

			{error && <div className="adm-query-error" role="alert"><AlertCircle size={16} />{error}</div>}
			{loading ? <div className="adm-commercial__empty">Loading unpaid records…</div> : filteredRows.length === 0 ? (
				<div className="adm-commercial__empty">{rows.length ? "No records match these filters." : "No unpaid customers at the moment."}</div>
			) : (
				<div className="adm-commercial__table-wrap">
					<table className="adm-commercial__table adm-unpaid__table">
						<thead><tr><th>Customer</th><th>Purchase</th><th>Payment</th><th>Created</th><th>Reference</th><th>Actions</th></tr></thead>
						<tbody>
							{filteredRows.map((row) => (
								<tr key={`${row.type}-${row.id}`}>
									<td><strong>{row.customer}</strong><small>{row.email || "No email"}{row.phone ? ` · ${row.phone}` : ""}</small></td>
									<td><strong>{row.item}</strong><small>{row.type} · {row.appointment || "—"}</small></td>
									<td><span className={`adm-unpaid__status is-${row.status}`}>{row.status}</span><small>{row.paymentMethod}</small></td>
									<td>{formatDate(row.createdAt)}</td>
									<td><code>{row.id}</code></td>
									<td>
										{row.canMarkOnPremPaid && (
											<button
												type="button"
												className="adm-unpaid__button adm-unpaid__button--secondary"
												onClick={() => markAppointmentPaid(row)}
												disabled={Boolean(markingPaidId)}
											>
												<Check size={15} />
												{markingPaidId === row.id ? "Saving…" : row.paymentMethod === "No online checkout · confirm payment method" ? "Record in-person payment" : "Mark paid"}
											</button>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
};

export default UnpaidCustomersTab;
